import React from "react";
import { Box } from "@mui/material";
import { VoucherCSVRow } from "../../../models/Vouchers/VoucherEmailer";
import CustomTypography from "../common/form/CustomTypography";

interface VoucherEmailPreviewProps {
  logoUrl?: string | null;
  message: string;
  sampleVoucher: VoucherCSVRow;
  template?: string;
  hideSwageazyLogo?: boolean;
}

const VoucherEmailPreview: React.FC<VoucherEmailPreviewProps> = ({
  logoUrl,
  message,
  sampleVoucher,
  template = "classic",
  hideSwageazyLogo = false,
}) => {
  if (template === "modern") {
    return (
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          paddingX: 2,
          paddingY: 1,
          borderRadius: 2,
          maxHeight: "500px",
          overflow: "auto",
          boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {!hideSwageazyLogo && (
          <Box sx={{ textAlign: "center" }}>
            <a
              href="https://www.swageazy.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://content.swageazy.com/logos/swageazy_logo_small.png"
                alt="Swageazy Logo"
                style={{
                  cursor: "pointer",
                  height: "60px",
                  width: "130px",
                }}
              />
            </a>
          </Box>
        )}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            margin: "0 auto",
            maxWidth: "500px",
            overflow: "hidden",
          }}
        >
          {logoUrl && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textAlign: "center",
                padding: "30px 20px",
              }}
            >
              <img
                src={logoUrl}
                alt="Customer Logo"
                style={{
                  maxHeight: "60px",
                  maxWidth: "120px",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              padding: "30px",
              textAlign: "center",
            }}
          >
            <CustomTypography
              variant="heading4"
              sx={{
                color: "#667eea",
                mb: 2,
              }}
            >
              🎉 You've Got a Gift!
            </CustomTypography>

            <Box
              sx={{
                lineHeight: 1.6,
                color: "#333",
                textAlign: "left",
                mb: 3,
                "& p": {
                  margin: "0 0 1em 0",
                },
                "& p:last-child": {
                  marginBottom: 0,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: message || "Your custom message will appear here...",
              }}
            />

            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                padding: "25px",
                margin: "20px auto",
                maxWidth: "350px",
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                Your Gift Card Code
              </CustomTypography>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "15px",
                  mb: 2,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: "#667eea",
                    fontFamily: "monospace",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                  }}
                >
                  {sampleVoucher["Gift Card Code"] || "SAMPLE-CODE-123"}
                </Box>
              </Box>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontSize: "12px",
                  opacity: 0.9,
                }}
              >
                Tap to copy • Valid until{" "}
                {sampleVoucher["Expiry Date"] || "31 Dec 2026"}
              </CustomTypography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "20px",
                margin: "20px auto",
                maxWidth: "350px",
                textAlign: "left",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "11px",
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Product
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#333",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {sampleVoucher["Product Name"] || "Sample Product"}
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "11px",
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Amount
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#667eea",
                      fontSize: "16px",
                      fontWeight: 700,
                    }}
                  >
                    {sampleVoucher["Currency Code"] || "INR"}{" "}
                    {sampleVoucher.Amount || "500"}
                  </CustomTypography>
                </Box>
              </Box>
              <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 2 }}>
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "11px",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Reference ID
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: "#333",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {sampleVoucher["Reference ID"] || "REF-12345"}
                </CustomTypography>
              </Box>
              {sampleVoucher["Term and condition"] && (
                <Box
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    mt: 2,
                    pt: 2,
                    textAlign: "center",
                  }}
                >
                  <Box
                    component="a"
                    href={sampleVoucher["Term and condition"]}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#667eea",
                      fontSize: "13px",
                      fontWeight: 600,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    View Terms & Conditions →
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              color: "#999",
              padding: "20px",
              textAlign: "center",
              fontSize: "11px",
            }}
          >
            © 2021 SuperV Technologies Pvt Ltd. All rights reserved.
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#e5e4e2",
        paddingX: 2,
        paddingY: 1,
        borderRadius: 2,
        maxHeight: "500px",
        overflow: "auto",
        boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      {!hideSwageazyLogo && (
        <Box sx={{ textAlign: "center" }}>
          <a
            href="https://www.swageazy.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://content.swageazy.com/logos/swageazy_logo_small.png"
              alt="Swageazy Logo"
              style={{
                cursor: "pointer",
                height: "60px",
                width: "130px",
              }}
            />
          </a>
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: "#f0efec",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
          margin: "0 auto",
          maxWidth: "500px",
          padding: "0 20px",
        }}
      >
        {logoUrl && (
          <Box sx={{ textAlign: "center", padding: "20px" }}>
            <img
              src={logoUrl}
              alt="Customer Logo"
              style={{
                maxHeight: "60px",
                maxWidth: "120px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: "#fff",
            padding: "20px 30px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              lineHeight: 1.5,
              color: "#000",
              fontWeight: 500,
              textAlign: "left",
              "& p": {
                margin: "0 0 1em 0",
              },
              "& p:last-child": {
                marginBottom: 0,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: message || "Your custom message will appear here...",
            }}
          />

          <Box
            sx={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              margin: "20px auto",
              padding: "15px",
              textAlign: "center",
              maxWidth: "300px",
            }}
          >
            <CustomTypography
              variant="body2"
              sx={{
                color: "#333",
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "10px",
              }}
            >
              Your Voucher Code
            </CustomTypography>
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px dashed #ccc",
                borderRadius: "4px",
                display: "inline-block",
                marginBottom: "10px",
                padding: "8px 15px",
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#003599",
                  fontFamily: "monospace",
                  fontSize: "18px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                }}
              >
                {sampleVoucher["Gift Card Code"] || "SAMPLE-CODE-123"}
              </Box>
            </Box>
            <Box sx={{ marginTop: "10px" }}>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                  margin: "0 0 5px 0",
                }}
              >
                Tap and hold to copy the code
              </CustomTypography>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                  margin: 0,
                }}
              >
                or select and copy manually
              </CustomTypography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              margin: "20px auto",
              padding: "15px",
              textAlign: "left",
              maxWidth: "300px",
            }}
          >
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Product:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Product Name"] || "Sample Product"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Amount:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Currency Code"] || "INR"}{" "}
                {sampleVoucher.Amount || "500"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Reference ID:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Reference ID"] || "REF-12345"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Expiry Date:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Expiry Date"] || "31 Dec 2026"}
              </Box>
            </Box>
            {sampleVoucher["Term and condition"] && (
              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  marginTop: "15px",
                  paddingTop: "15px",
                  textAlign: "center",
                }}
              >
                <Box
                  component="a"
                  href={sampleVoucher["Term and condition"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#003599",
                    fontSize: "13px",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View Terms & Conditions
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            color: "#838383",
            padding: "20px",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          © 2021 SuperV Technologies Pvt Ltd. All rights reserved.
        </Box>
      </Box>
    </Box>
  );
};

export default VoucherEmailPreview;
